import type { UserMode } from '@respiria/shared';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { TourOverlay } from './TourOverlay';

/**
 * Provides `startTour(mode)` to any tab-bar screen and renders the coach-mark
 * overlay when a tour is active (issue #57). Mounted once in AppLayout so the
 * overlay sits above the tab bar and any page.
 */
type TourContextValue = { startTour: (mode: UserMode) => void };

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [activeMode, setActiveMode] = useState<UserMode | null>(null);
  const startTour = useCallback((mode: UserMode) => setActiveMode(mode), []);
  const value = useMemo(() => ({ startTour }), [startTour]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeMode && <TourOverlay mode={activeMode} onClose={() => setActiveMode(null)} />}
    </TourContext.Provider>
  );
}
